/**
 * Recipe mods: where they live and how they reach the engine.
 *
 * A recipe (recipe.ts) is a JSON document of field-level changes. The drafts
 * the player authors live in vault *meta* — never as game files — and the
 * compiled overlay exists only in MEMFS, rebuilt on every boot:
 *
 *   boot → gameFiles(FS) over the freshly-mounted vault
 *        → merge(enabled drafts) → compile(merged, effective base)
 *        → write mods/rr-recipes/<path> into MEMFS
 *        → ensure `rr-recipes` is the last mods_order.txt line
 *        → callMain()
 *
 * Compiling per boot instead of installing compiled bytes into the vault is
 * the point, not a shortcut. A recipe says "set maxDamage to 50 on whatever
 * base you have"; the base *changes* — the 1.02 patch arrives, the Restoration
 * Project is installed or removed — and a compiled items.lst baked against one
 * base is quietly wrong on the next. Recompiling means the recipe always says
 * its thing about today's base, disabling a mod is a flag flip with no vault
 * surgery, and the vault never holds a byte the player didn't import.
 *
 * All enabled recipes compile as ONE merged document into ONE folder mod.
 * Separate folders would put two whole items.lst files in the chain and the
 * loader would keep only the later one (whole-file replacement, angle 4);
 * merge() is what lets two recipes' new items coexist. Same-field conflicts
 * come back as data and are surfaced in the Mod Maker, load order winning.
 *
 * Failure posture: nothing here may stop a boot. A recipe that no longer
 * compiles (its target item vanished with the mod that provided it) is
 * reported through the last-compile record and skipped.
 */

import type { EmFS } from '../engine';
import { activeHome } from '../config';
import { gameFiles, resetGameFilesCache, type GameFiles } from '../gamedata/files';
import { resetInstallDataCache } from '../gamedata';
import { getMeta, setMeta } from '../store';
import { claimedNumbers, parseLst } from './lst-codec';
import {
  assertValid,
  merge,
  compile,
  NEW_ITEM_BAND,
  RecipeError,
  type Conflict,
  type Recipe,
} from './recipe';

/** The one folder mod every enabled recipe compiles into. */
export const RECIPES_MOUNT_ID = 'rr-recipes';

const DRAFTS_KEY = 'recipes';
const LAST_COMPILE_KEY = 'recipesLastCompile';

export interface RecipeDraft {
  recipe: Recipe;
  enabled: boolean;
  createdAt: number;
  updatedAt: number;
}

/** What the last boot's compile did — the Mod Maker's status line. */
export interface LastCompile {
  at: number;
  /** Recipe ids that compiled in. */
  applied: string[];
  /** Recipe ids skipped, with the reason. */
  skipped: { id: string; reason: string }[];
  warnings: string[];
  conflicts: Conflict[];
  fileCount: number;
}

export async function listDrafts(): Promise<RecipeDraft[]> {
  return (await getMeta<RecipeDraft[]>(DRAFTS_KEY)) ?? [];
}

export async function getLastCompile(): Promise<LastCompile | undefined> {
  return getMeta<LastCompile>(LAST_COMPILE_KEY);
}

/** Upserts by recipe id. Validates first; an invalid draft is refused whole. */
export async function saveDraft(recipe: Recipe, enabled?: boolean): Promise<RecipeDraft> {
  assertValid(recipe);
  const drafts = await listDrafts();
  const now = Date.now();
  const existing = drafts.find((d) => d.recipe.id === recipe.id);
  let saved: RecipeDraft;
  if (existing) {
    existing.recipe = recipe;
    existing.updatedAt = now;
    if (enabled !== undefined) existing.enabled = enabled;
    saved = existing;
  } else {
    saved = { recipe, enabled: enabled ?? true, createdAt: now, updatedAt: now };
    drafts.push(saved);
  }
  await setMeta(DRAFTS_KEY, drafts);
  return saved;
}

export async function setDraftEnabled(id: string, enabled: boolean): Promise<void> {
  const drafts = await listDrafts();
  const draft = drafts.find((d) => d.recipe.id === id);
  if (!draft) throw new RecipeError(`No recipe with id "${id}".`);
  draft.enabled = enabled;
  await setMeta(DRAFTS_KEY, drafts);
}

export async function deleteDraft(id: string): Promise<void> {
  const drafts = await listDrafts();
  await setMeta(
    DRAFTS_KEY,
    drafts.filter((d) => d.recipe.id !== id)
  );
}

/**
 * The lowest free proto number in the reserved band, counting both what the
 * effective items.lst already claims (vanilla runs to ~605; a big overhaul
 * could reach further) and what every draft has already reserved — a draft's
 * claim must hold even while the draft is disabled, or re-enabling it would
 * collide with the item made in the meantime.
 */
export async function allocatePid(files: GameFiles, drafts: RecipeDraft[]): Promise<number> {
  const taken = new Set<number>();
  const lstBytes = await files.read('proto/items/items.lst');
  if (lstBytes) for (const n of claimedNumbers(parseLst(lstBytes))) taken.add(n);
  for (const d of drafts) for (const item of d.recipe.newItems ?? []) taken.add(item.pid);
  for (let pid = NEW_ITEM_BAND[0]; pid <= NEW_ITEM_BAND[1]; pid++) {
    if (!taken.has(pid)) return pid;
  }
  throw new RecipeError('The reserved pid band 4000-9999 is full.');
}

// ------------------------------------------------------------------- the boot lane

const decodeLatin1 = (bytes: Uint8Array): string => {
  let s = '';
  for (let i = 0; i < bytes.length; i += 8192) {
    s += String.fromCharCode(...bytes.subarray(i, Math.min(i + 8192, bytes.length)));
  }
  return s;
};

function readMemfs(FS: EmFS, path: string): Uint8Array | null {
  try {
    const data = FS.readFile(path, { encoding: 'binary' });
    return data instanceof Uint8Array ? data : null;
  } catch {
    return null;
  }
}

function writeMemfs(FS: EmFS, path: string, bytes: Uint8Array | string): void {
  const dir = path.slice(0, path.lastIndexOf('/'));
  if (typeof FS.mkdirTree === 'function') FS.mkdirTree(dir);
  FS.writeFile(path, bytes);
}

/**
 * The mods_order.txt the engine will read, with our mount as the LAST line —
 * dbOpen prepends, so last is highest priority, above the Restoration
 * Project's own archives when they are present.
 *
 * When no order file exists we synthesize one listing any mods/*.dat first
 * (exactly what sfallLoadMods would have auto-generated) so creating the file
 * never silences a mod the auto-generation would have found.
 */
function orderWithRecipes(FS: EmFS): string {
  const HOME = activeHome();
  const existing = readMemfs(FS, `${HOME}/mods/mods_order.txt`);
  let lines: string[];
  if (existing) {
    lines = decodeLatin1(existing).split('\n').map((l) => l.replace(/\r$/, ''));
    while (lines.length && lines[lines.length - 1]!.trim() === '') lines.pop();
    lines = lines.filter((l) => l.trim().toLowerCase() !== RECIPES_MOUNT_ID);
  } else {
    let dats: string[] = [];
    try {
      dats = (FS.readdir(`${HOME}/mods`) as string[]).filter((n) => /\.dat$/i.test(n)).sort();
    } catch {
      /* no mods directory yet */
    }
    lines = dats;
  }
  lines.push(RECIPES_MOUNT_ID);
  return lines.join('\n') + '\n';
}

/**
 * Compiles every enabled recipe into MEMFS. Called between the vault mount and
 * callMain(); never throws. Returns the record it also stores in meta.
 */
export async function applyRecipesToBoot(FS: EmFS): Promise<LastCompile | null> {
  let drafts: RecipeDraft[];
  try {
    drafts = (await listDrafts()).filter((d) => d.enabled);
  } catch {
    return null;
  }
  if (!drafts.length) {
    await setMeta(LAST_COMPILE_KEY, undefined).catch(() => {});
    return null;
  }

  const record: LastCompile = { at: Date.now(), applied: [], skipped: [], warnings: [], conflicts: [], fileCount: 0 };

  try {
    const files = await gameFiles(FS);
    if (!files.ok) throw new RecipeError('no readable game files');

    // Recipes that don't merge or compile are dropped one at a time, oldest
    // kept first, so one broken draft costs itself and nothing else.
    const usable = [...drafts];
    for (;;) {
      if (!usable.length) break;
      try {
        const { merged, conflicts } = merge(
          usable.map((d) => d.recipe),
          { id: RECIPES_MOUNT_ID, name: 'Rerun Reactor recipes', version: '1.0.0' }
        );
        const result = await compile(merged, files.read);
        const home = activeHome();
        for (const f of result.files) writeMemfs(FS, `${home}/mods/${RECIPES_MOUNT_ID}/${f.path}`, f.bytes);
        writeMemfs(FS, `${home}/mods/mods_order.txt`, orderWithRecipes(FS));
        record.applied = usable.map((d) => d.recipe.id);
        record.conflicts = conflicts;
        record.warnings = result.warnings;
        record.fileCount = result.files.length;
        break;
      } catch (e) {
        const dropped = usable.pop()!;
        record.skipped.push({ id: dropped.recipe.id, reason: e instanceof Error ? e.message : String(e) });
      }
    }
  } catch (e) {
    record.skipped = drafts.map((d) => ({
      id: d.recipe.id,
      reason: e instanceof Error ? e.message : String(e),
    }));
  }

  // The memoised view predates the overlay; whoever reads next (the save
  // editor's catalog, most likely) must see the recipes' items.
  resetGameFilesCache();
  resetInstallDataCache();

  await setMeta(LAST_COMPILE_KEY, record).catch(() => {});
  return record;
}
